import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { BubbleMenu, FloatingMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import MarkdownIt from 'markdown-it';
import TurndownService from 'turndown';

const renderer = new MarkdownIt({ html: false, breaks: true, linkify: false });
renderer.renderer.rules.image = () => '';
const converter = new TurndownService({ headingStyle: 'atx', bulletListMarker: '-', hr: '---' });
converter.remove(['script', 'style', 'iframe', 'img']);
export const markdownToHtml = (value) => renderer.render(value || '');
export function savedDocumentMarkdown(doc, type) {
  if (typeof doc?.editedMarkdown === 'string') return doc.editedMarkdown;
  if (doc?.editedHtml) return converter.turndown(doc.editedHtml);
  const text = doc?.editedText ?? doc?.content ?? '';
  if (doc?.contentFormat === 'markdown' || type !== 'cv' || !text) return text;
  // Migrate older plain-text drafts without rewriting their wording.
  return text.split('\n').map((line, index) => index === 0 ? `# ${line}` : /^[A-Z][A-Z &]{2,35}$/.test(line.trim()) ? `\n## ${line.trim()}\n` : line).join('\n');
}

const bubbleOptions = { placement: 'top', offset: 8 };
const floatingOptions = { placement: 'left', offset: 8 };
const blockOptions = [
  ['Text', (chain) => chain.setParagraph()],
  ['Section heading', (chain) => chain.setHeading({ level: 2 })],
  ['Subheading', (chain) => chain.setHeading({ level: 3 })],
  ['Bullet list', (chain) => chain.toggleBulletList()],
  ['Numbered list', (chain) => chain.toggleOrderedList()],
  ['Divider', (chain) => chain.setHorizontalRule()],
];

export default function MarkdownDocument({ value, onChange, type, disabled }) {
  const [blockMenu, setBlockMenu] = useState(null);
  const [activeOption, setActiveOption] = useState(0);
  const menuRef = useRef(null);
  const actionRef = useRef(null);
  const dismissedSlash = useRef('');
  const lastEmitted = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const label = type === 'cv' ? 'CV' : 'Cover letter';
  const initialHtml = useRef(markdownToHtml(value));
  const extensions = useMemo(() => [StarterKit.configure({ heading: { levels: [1, 2, 3] }, codeBlock: false, code: false, strike: false, underline: false, link: { openOnClick: false, autolink: false } })], []);
  const editorProps = useMemo(() => ({
      attributes: { class: 'opp-document-content', role: 'textbox', 'aria-label': `${label} editor`, 'aria-multiline': 'true', spellcheck: 'true' },
      handleKeyDown: (_view, event) => {
        if (!menuRef.current) return false;
        if (event.key === 'Escape') { dismissedSlash.current = menuRef.current.signature; setBlockMenu(null); return true; }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          const count = blockOptions.filter(([name]) => name.toLowerCase().includes(menuRef.current.query)).length;
          setActiveOption((index) => count ? (index + (event.key === 'ArrowDown' ? 1 : -1) + count) % count : 0);
          return true;
        }
        if (event.key === 'Enter') { actionRef.current?.(); return true; }
        return false;
      },
    }), [label]);
  const editor = useEditor({
    extensions, content: initialHtml.current, editable: !disabled, editorProps,
    onUpdate: ({ editor: current }) => {
      const markdown = converter.turndown(current.getHTML());
      lastEmitted.current = markdown;
      onChangeRef.current(markdown);
    },
    onTransaction: ({ editor: current }) => {
      const { $from, empty } = current.state.selection;
      const match = empty && $from.parent.type.name === 'paragraph' && $from.parent.textContent.match(/^\/([a-z ]*)$/i);
      if (!match) { setBlockMenu(null); dismissedSlash.current = ''; return; }
      const signature = `${$from.start()}:${match[0]}`;
      if (dismissedSlash.current === signature) return;
      const coords = current.view.coordsAtPos($from.start());
      const root = current.view.dom.closest('.opp-inline-document').getBoundingClientRect();
      const next = { from: $from.start(), to: $from.end(), query: match[1].toLowerCase(), signature, top: coords.bottom - root.top + 6, left: Math.min(coords.left - root.left, Math.max(0, root.width - 240)) };
      setBlockMenu((current) => current?.signature === next.signature && current.top === next.top && current.left === next.left ? current : next);
      if (menuRef.current?.query !== match[1].toLowerCase()) setActiveOption(0);
    },
  });
  const showBubble = useCallback(({ editor: current, state }) => !disabled && current.isFocused && !state.selection.empty, [disabled]);
  const showFloating = useCallback(({ editor: current, state }) => !disabled && current.isFocused && state.selection.empty && state.selection.$from.parent.type.name === 'paragraph' && !state.selection.$from.parent.textContent, [disabled]);
  menuRef.current = blockMenu;
  useEffect(() => {
    if (editor && value !== lastEmitted.current) {
      editor.commands.setContent(markdownToHtml(value), { emitUpdate: false });
      lastEmitted.current = value;
    }
  }, [editor, value]);
  useEffect(() => { editor?.setEditable(!disabled); }, [editor, disabled]);
  const applyBlock = (option, slash = false) => {
    if (!editor || disabled) return;
    let chain = editor.chain().focus();
    if (slash && blockMenu) chain = chain.deleteRange({ from: blockMenu.from, to: blockMenu.to });
    option[1](chain).run();
    setBlockMenu(null);
  };
  const options = blockOptions.filter(([name]) => name.toLowerCase().includes(blockMenu?.query || ''));
  actionRef.current = () => { if (options[activeOption]) applyBlock(options[activeOption], true); };
  return <div className="opp-inline-document">
    {editor && <>
      <BubbleMenu editor={editor} options={bubbleOptions} className="opp-document-bubble" shouldShow={showBubble}>
        <button type="button" aria-label="Bold" onMouseDown={(event) => event.preventDefault()} onClick={() => editor.chain().focus().toggleBold().run()}><strong>B</strong></button>
        <button type="button" aria-label="Italic" onMouseDown={(event) => event.preventDefault()} onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></button>
        <span />
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => applyBlock(blockOptions[1])}>Heading</button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => applyBlock(blockOptions[3])}>• List</button>
      </BubbleMenu>
      <FloatingMenu editor={editor} options={floatingOptions} className="opp-document-add" shouldShow={showFloating}>
        <button type="button" aria-label="Add a block" title="Add a block" onMouseDown={(event) => event.preventDefault()} onClick={() => editor.chain().focus().insertContent('/').run()}>+</button>
      </FloatingMenu>
    </>}
    <div className={`opp-markdown-preview is-${type}`}><EditorContent editor={editor} /></div>
    {blockMenu && !disabled && <div className="opp-document-block-menu" role="menu" aria-label="Insert document block" style={{ top: blockMenu.top, left: blockMenu.left }}>{options.length ? options.map((option, index) => <button type="button" role="menuitem" className={index === activeOption ? 'is-active' : ''} key={option[0]} onMouseDown={(event) => event.preventDefault()} onClick={() => applyBlock(option, true)}>{option[0]}</button>) : <p>No matching blocks</p>}</div>}
  </div>;
}
